import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ChevronDown, Construction, Wallet, Zap } from 'lucide-react';
import {
  formatValidUntil,
  pluralCredits,
  type CreditLotSource,
  type CreditsResponse,
  type PackagePublic,
  type PackagesListResponse,
  type Pricing,
  type ProfileResponse,
} from '@masha/shared';
import { api, ApiError } from '../lib/api';
import { openExternalLink } from '../lib/tma';
import { Button, Card, Spinner, Toast } from '../components/ui';

const formatKzt = (n: number) => new Intl.NumberFormat('ru-KZ').format(n) + ' ₸';

const credits = (n: number) => `${n} ${pluralCredits(n)}`;

function pluralDays(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'день';
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'дня';
  return 'дней';
}

const SOURCE_LABEL: Record<CreditLotSource, string> = {
  purchase: 'Покупка',
  bonus_onboarding: 'Бонус за анкету',
  bonus_referral: 'Бонус за друга',
  migration: 'Перенесённый баланс',
  manual: 'Начисление',
  refund: 'Возврат',
  bonus_survey: 'Бонус за опрос',
};

const PROMO_ERROR_TEXT: Record<string, string> = {
  PROMO_NOT_FOUND: 'Промокод не найден',
  PROMO_EXPIRED: 'Срок действия промокода истёк',
  PROMO_ALREADY_USED: 'Ты уже активировал этот код',
  PROMO_LIMIT_REACHED: 'Активации по этому коду закончились',
  RATE_LIMITED: 'Слишком много попыток, подожди минуту',
  PROMO_NOT_STARTED: 'Промокод начнёт действовать позже',
};

function pricingLine(p: Pricing): string {
  return `Сценарий — ${credits(p.reels)} · карусель — ${credits(p.carousel)} · ролик в дубликаторе — ${credits(p.uniq)}`;
}

export function BalancePage() {
  const qc = useQueryClient();

  useEffect(() => {
    qc.invalidateQueries({ queryKey: ['profile'] });
    qc.invalidateQueries({ queryKey: ['credits'] });
  }, [qc]);

  const profileQuery = useQuery<ProfileResponse>({ queryKey: ['profile'] });
  const packagesQuery = useQuery<PackagesListResponse>({
    queryKey: ['packages'],
    queryFn: () => api.listPackages(),
  });
  const creditsQuery = useQuery<CreditsResponse>({
    queryKey: ['credits'],
    queryFn: () => api.getCredits(),
  });

  const [toast, setToast] = useState<string | null>(null);
  const [pendingPackageId, setPendingPackageId] = useState<number | null>(null);
  const [lotsOpen, setLotsOpen] = useState(false);

  // Промокод: скидка привязана к конкретному пакету, турбо включается сразу
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState<{
    code: string;
    packageId: number;
    finalPriceKzt: number;
  } | null>(null);
  // A code can open a hidden promo tariff (e.g. turbo); its card comes from
  // the promo response itself, since it may not be in the public package list.
  const [promoPackage, setPromoPackage] = useState<PackagePublic | null>(null);

  function showToast(message: string) {
    setToast(message);
    setTimeout(() => setToast(null), 3000);
  }

  const createPayment = useMutation({
    mutationFn: (input: { packageId: number; promoCode?: string }) => api.createPayment(input),
    onMutate: (input) => {
      setPendingPackageId(input.packageId);
    },
    onSuccess: (data) => {
      window.location.href = data.paymentUrl;
      // pendingPackageId намеренно не сбрасываем — браузер вот-вот уйдёт на Robokassa
    },
    onError: (err) => {
      setPendingPackageId(null);
      const code = err instanceof ApiError ? err.code : 'UNKNOWN';
      let message = 'Не удалось создать платёж';
      if (code === 'ROBOKASSA_NOT_CONFIGURED') {
        message = 'Оплата временно недоступна, попробуйте позже';
      } else if (code === 'INVALID_PROMO_CODE') {
        message = 'Промокод больше не действует';
        setAppliedPromo(null);
      } else if (code === 'PACKAGE_NOT_FOUND' && appliedPromo) {
        // Promo-only hidden package (e.g. turbo) whose window closed between
        // "Применить" and "Купить": the API can no longer find it.
        message = 'Промокод больше не действует';
        setAppliedPromo(null);
        setPromoPackage(null);
      }
      showToast(message);
    },
  });

  const applyPromo = useMutation({
    mutationFn: (code: string) => api.applyPromo({ code }),
    onSuccess: (data, code) => {
      if (data.kind === 'discount') {
        setAppliedPromo({ code, packageId: data.packageId, finalPriceKzt: data.finalPriceKzt });
        setPromoPackage(data.package);
        showToast('Промокод применён');
        return;
      }
      setAppliedPromo(null);
      setPromoPackage(null);
      setPromoInput('');
      qc.invalidateQueries({ queryKey: ['profile'] });
      qc.invalidateQueries({ queryKey: ['credits'] });
      showToast(`Турбо включён до ${formatValidUntil(data.turboUntil)} включительно`);
    },
    onError: (err) => {
      setAppliedPromo(null);
      setPromoPackage(null);
      const code = err instanceof ApiError ? err.code : 'UNKNOWN';
      showToast(PROMO_ERROR_TEXT[code] ?? 'Не удалось применить промокод');
    },
  });

  function handleApplyPromo() {
    const code = promoInput.trim();
    if (code) applyPromo.mutate(code);
  }

  function handleBuy(pkg: PackagePublic) {
    const promoCode = appliedPromo?.packageId === pkg.id ? appliedPromo.code : undefined;
    createPayment.mutate({ packageId: pkg.id, promoCode });
  }

  const user = profileQuery.data?.user;
  const balance = user?.balanceCredits ?? null;
  const nextExpiry = user?.creditsNextExpiry ?? null;
  const turboUntil =
    user?.turboUntil && new Date(user.turboUntil).getTime() > Date.now() ? user.turboUntil : null;
  const pricing = packagesQuery.data?.pricing ?? null;
  const packagesList = packagesQuery.data?.packages ?? [];
  // A code can open a hidden promo tariff: show its card after the listed ones.
  const visiblePackages =
    promoPackage && !packagesList.some((p) => p.id === promoPackage.id)
      ? [...packagesList, promoPackage]
      : packagesList;
  const lots = creditsQuery.data?.lots ?? [];

  return (
    <div className="min-h-screen p-4 max-w-lg mx-auto pb-12 safe-top safe-bottom">
      <header className="flex items-center justify-between mb-4">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-tg-hint hover:text-tg-text transition-colors"
        >
          <ArrowLeft size={16} />
          Назад
        </Link>
        <h1 className="text-lg font-semibold">Пополнение</h1>
        <span className="w-12" />
      </header>

      <Card variant="elevated" padding="lg" className="mb-4 text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-brand-muted text-brand mb-3">
          <Wallet size={24} />
        </div>
        <p className="text-xs uppercase tracking-wider text-tg-hint mb-1">Текущий баланс</p>
        <p className="text-3xl font-bold mb-1 tabular-nums">
          {balance ?? '—'}
          <span className="text-sm font-medium text-tg-hint ml-2">
            {balance !== null ? pluralCredits(balance) : 'кредитов'}
          </span>
        </p>
        {nextExpiry && balance !== null && (
          <button
            type="button"
            onClick={() => setLotsOpen((o) => !o)}
            className="inline-flex items-center gap-1 text-xs text-tg-hint"
          >
            {nextExpiry.credits === balance
              ? `Действуют до ${formatValidUntil(nextExpiry.expiresAt)} включительно`
              : `${nextExpiry.credits} из них сгорят ${formatValidUntil(nextExpiry.expiresAt)} в 23:59`}
            <ChevronDown
              size={12}
              className={lotsOpen ? 'rotate-180 transition-transform' : 'transition-transform'}
            />
          </button>
        )}
        {pricing && <p className="text-xs text-tg-hint mt-1">{pricingLine(pricing)}</p>}
        {lotsOpen && lots.length > 0 && (
          <ul className="mt-3 text-left divide-y divide-tg-hint/10">
            {lots.map((l, i) => (
              <li key={i} className="flex items-center justify-between gap-3 py-2 text-sm">
                <span>{SOURCE_LABEL[l.source]}</span>
                <span className="tabular-nums text-xs text-tg-hint whitespace-nowrap">
                  {l.remaining} из {l.amount} · до {formatValidUntil(l.expiresAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {turboUntil && (
        <Card variant="surface" padding="md" className="mb-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-brand-muted text-brand flex items-center justify-center">
              <Zap size={20} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold">
                Турбо до {formatValidUntil(turboUntil)} включительно
              </p>
              <p className="text-xs text-tg-hint leading-snug">
                Сценарии, карусели и дубликатор без списания кредитов
              </p>
            </div>
          </div>
        </Card>
      )}

      {packagesQuery.isLoading && (
        <div className="flex flex-col items-center py-8">
          <Spinner />
          <p className="text-sm text-tg-hint mt-3">Загружаем пакеты</p>
        </div>
      )}

      {packagesQuery.isError && (
        <div className="text-center py-8">
          <p className="text-sm text-tg-hint mb-3">Не удалось загрузить пакеты</p>
          <Button onClick={() => packagesQuery.refetch()} variant="primary">
            Повторить
          </Button>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        <input
          type="text"
          value={promoInput}
          onChange={(e) => setPromoInput(e.target.value)}
          placeholder="Промокод"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          className="flex-1 min-w-0 rounded-xl bg-tg-section-bg px-4 py-2.5 text-sm outline-none placeholder:text-tg-hint"
        />
        <Button
          onClick={handleApplyPromo}
          variant="secondary"
          disabled={!promoInput.trim() || applyPromo.isPending}
        >
          {applyPromo.isPending ? '...' : 'Применить'}
        </Button>
      </div>
      <p className="text-xs text-tg-hint -mt-2 mb-4">Промокод с вебинара откроет тариф «Турбо»</p>

      {visiblePackages.length > 0 && (
        <div className="grid grid-cols-1 gap-3">
          {visiblePackages.map((pkg) => (
            <PackageCard
              key={pkg.id}
              pkg={pkg}
              promoPriceKzt={appliedPromo?.packageId === pkg.id ? appliedPromo.finalPriceKzt : null}
              onBuy={handleBuy}
              isPending={pendingPackageId === pkg.id}
              isAnyPending={pendingPackageId !== null}
            />
          ))}
        </div>
      )}

      {visiblePackages.length > 0 && (
        <p className="text-xs text-tg-hint text-center mt-4">
          Нажимая «Купить», ты принимаешь условия{' '}
          <button
            type="button"
            className="text-tg-link underline"
            onClick={() => openExternalLink(`${window.location.origin}/offer.html`)}
          >
            публичной оферты
          </button>
        </p>
      )}

      {toast && (
        <Toast tone="brand" placement="fixed">
          <span className="inline-flex items-center gap-2">
            <Construction size={16} />
            {toast}
          </span>
        </Toast>
      )}
    </div>
  );
}

function PackageCard({
  pkg,
  promoPriceKzt,
  onBuy,
  isPending,
  isAnyPending,
}: {
  pkg: PackagePublic;
  promoPriceKzt: number | null;
  onBuy: (pkg: PackagePublic) => void;
  isPending: boolean;
  isAnyPending: boolean;
}) {
  return (
    <Card variant="surface" padding="md">
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold mb-0.5 leading-tight">{pkg.name}</h3>
          {pkg.description && (
            <p className="text-xs text-tg-hint leading-snug">{pkg.description}</p>
          )}
          <p className="text-xs text-tg-hint mt-1 tabular-nums">
            {pkg.kind === 'turbo'
              ? `Безлимит на ${pkg.turboDays ?? 0} ${pluralDays(pkg.turboDays ?? 0)}`
              : `${credits(pkg.credits)} · действуют ${pkg.validityDays} ${pluralDays(pkg.validityDays)}`}
          </p>
        </div>
        <div className="text-right shrink-0 flex flex-col items-end gap-2">
          {promoPriceKzt !== null && promoPriceKzt < pkg.priceKzt ? (
            <p className="text-sm font-semibold whitespace-nowrap tabular-nums">
              <span className="text-tg-hint line-through font-normal mr-1.5">
                {formatKzt(pkg.priceKzt)}
              </span>
              {formatKzt(promoPriceKzt)}
            </p>
          ) : (
            <p className="text-sm font-semibold whitespace-nowrap tabular-nums">
              {formatKzt(promoPriceKzt ?? pkg.priceKzt)}
            </p>
          )}
          <Button onClick={() => onBuy(pkg)} variant="primary" size="sm" disabled={isAnyPending}>
            {isPending ? '...' : 'Купить'}
          </Button>
        </div>
      </div>
    </Card>
  );
}
